/**
=========================================================
* Argon Dashboard 2 MUI - v3.0.1
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-dashboard-material-ui
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// @mui material components

import Card from "@mui/material/Card";

// Argon Dashboard 2 MUI components
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";

// Argon Dashboard 2 MUI examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import Table from "examples/Tables/Table";
import Grid from "@mui/material/Grid";

import breakpoints from "assets/theme/base/breakpoints";

// Data
import ShortListTutorsTableData from "Views/Activities/ShortListTutors/data/ShortListTableData";
import {
  CardContent,
  CardHeader,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Slider,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Autocomplete,
  TextField,
  Chip,
} from "@mui/material";
import ArgonButton from "components/ArgonButton";
// import ReferralTableData from "Views/Activities/Referral/data/ReferralTableData"
import React, { useState, useEffect, useRef } from "react";
import Dropdown from "components/CustomDropdown/Dropdown";
import api from "utils/api";
import {
  GoogleMap,
  MarkerF,
  InfoWindowF,
  useLoadScript,
  OverlayView,
} from "@react-google-maps/api";
import { CircularProgress, Alert, Snackbar } from "@mui/material";
import ArgonAvatar from "components/ArgonAvatar";
import ArgonBadge from "components/ArgonBadge";
import defaultAvatar from "assets/images/team-2.jpg";
import { Link } from "react-router-dom";

// Google Maps API Key - Replace with your own key
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "YOUR_GOOGLE_MAPS_API_KEY";

function Tables() {
  // Load Google Maps script
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
  });

  const { columns, rows } = ShortListTutorsTableData;

  const [Enquiry, setEnquiry] = useState("");
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [Status, setStatus] = useState("");
  const [Academic, setAcademic] = useState("");
  const [Gender, setGender] = useState("");
  const [BadgeLevel, setBadgeLevel] = useState("");
  const [fluency, setFluency] = useState(0);
  const [radius, setRadius] = useState(0);
  const [parents, setParents] = useState([]);
  const [parentDetails, setParentDetails] = useState(null);
  const [students, setStudents] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [filteredTutors, setFilteredTutors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showAlert, setShowAlert] = useState(false);

  // Map states
  const [mapCenter, setMapCenter] = useState({ lat: 28.6139, lng: 77.209 }); // Default to Delhi
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [mapZoom, setMapZoom] = useState(13);
  const mapRef = useRef(null);
  const [selectedMarker, setSelectedMarker] = useState(null);

  useEffect(() => {
    const fetchParents = async () => {
      try {
        const response = await api.get("/parent");
        if (response.data.success) {
          const parentOptions = response.data.data.map((parent) => ({
            label: parent.fullName,
            value: parent._id,
            location: parent.location,
          }));
          setParents([{ label: "Select Parent", value: "" }, ...parentOptions]);
        }
      } catch (error) {
        console.error("Error fetching parents:", error);
      }
    };
    fetchParents();
  }, []);

  // Fetch parent details and update map when parent is selected
  useEffect(() => {
    const fetchParentDetails = async () => {
      if (!Enquiry || Enquiry === "") {
        setParentDetails(null);
        setMapCenter({ lat: 28.6139, lng: 77.209 });
        setSelectedLocation(null);
        return;
      }

      try {
        const response = await api.get(`/parent?id=${Enquiry}`);

        if (response.data.success) {
          const parent = response.data.data;
          setParentDetails(parent);

          // Set map center and marker to parent's location
          // Database format: [latitude, longitude]
          if (
            parent &&
            parent.location &&
            parent.location.coordinates &&
            parent.location.coordinates.length === 2
          ) {
            const [lat, lng] = parent.location.coordinates;
            if (lat && lng && !isNaN(lat) && !isNaN(lng)) {
              const newCenter = { lat, lng };
              setMapCenter(newCenter);
              setSelectedLocation(newCenter);
            } else {
              console.warn("Invalid coordinates for parent:", parent.location.coordinates);
            }
          } else {
            console.warn("Parent location not available or invalid:", parent?.location);
          }
        }
      } catch (error) {
        console.error("Error fetching parent details:", error);
      }
    };
    fetchParentDetails();
  }, [Enquiry]);

  // Fetch students when parent is selected
  useEffect(() => {
    const fetchStudents = async () => {
      if (!Enquiry) {
        setStudents([]);
        setSelectedStudents([]);
        setSubjects([]);
        return;
      }

      try {
        const response = await api.get(`/student?parentId=${Enquiry}`);
        if (response.data.success) {
          const studentOptions = response.data.data.map((student) => ({
            label: student.name,
            value: student._id,
            ...student,
          }));
          setStudents(studentOptions);
        }
      } catch (error) {
        console.error("Error fetching students:", error);
      }
    };
    fetchStudents();
  }, [Enquiry]);

  // Fetch subjects when students are selected
  useEffect(() => {
    const fetchSubjectsForStudents = async () => {
      if (!selectedStudents || selectedStudents.length === 0) {
        setSubjects([{ label: "Select Subject", value: "" }]);
        setAcademic("");
        return;
      }

      try {
        // Collect unique subjects from all selected students
        const allSubjects = new Map();

        for (const studentId of selectedStudents) {
          const response = await api.get(`/student?id=${studentId}`);
          if (response.data.success) {
            const studentData = response.data.data;
            if (studentData.subjects && Array.isArray(studentData.subjects)) {
              studentData.subjects.forEach((subject) => {
                if (subject._id && subject.name) {
                  allSubjects.set(subject._id, {
                    label: subject.name,
                    value: subject._id,
                  });
                }
              });
            }
          }
        }

        const subjectOptions = Array.from(allSubjects.values());
        setSubjects([{ label: "Select Subject", value: "" }, ...subjectOptions]);
      } catch (error) {
        console.error("Error fetching student details:", error);
        setSubjects([{ label: "Select Subject", value: "" }]);
      }
    };
    fetchSubjectsForStudents();
  }, [selectedStudents]);

  // Apply filters and fetch tutors
  const handleApplyFilters = async () => {
    if (!Enquiry || !selectedStudents || selectedStudents.length === 0) {
      setError("Please select a parent and at least one student");
      setShowAlert(true);
      return;
    }

    // Validate that parent has location if radius is being used
    if (radius > 0 && !selectedLocation) {
      setError("Parent location not available. Cannot apply radius filter.");
      setShowAlert(true);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Fetch tutors for each student and combine results
      const allTutorsMap = new Map();

      for (const studentId of selectedStudents) {
        const params = {
          parentId: Enquiry,
          studentId: studentId,
          page: 1,
          limit: 100,
        };

        if (Academic && Academic !== "") {
          params.subjects = Academic;
        }

        if (Gender && Gender !== "") {
          params.gender = Gender;
        }

        if (BadgeLevel && BadgeLevel !== "All" && BadgeLevel !== "") {
          params.tutorLevel = BadgeLevel;
        }

        if (fluency > 0) {
          params.englishProficiency = fluency;
        }

        // Use parent's location for radius filtering
        if (radius > 0 && selectedLocation) {
          params.radius = radius;
          params.lat = selectedLocation.lat;
          params.long = selectedLocation.lng;
        }

        const response = await api.get("/tutor/filter-for-parent", { params });

        if (response.data.success && response.data.data) {
          response.data.data.forEach((tutor) => {
            if (!allTutorsMap.has(tutor._id)) {
              allTutorsMap.set(tutor._id, tutor);
            }
          });
        }
      }

      const uniqueTutors = Array.from(allTutorsMap.values());

      // Sort by distance if radius was applied
      if (radius > 0 && selectedLocation) {
        uniqueTutors.sort((a, b) => {
          const distA = a.distance || Infinity;
          const distB = b.distance || Infinity;
          return distA - distB;
        });
      }

      setFilteredTutors(uniqueTutors);

      // Adjust map to show all markers
      if (uniqueTutors.length > 0 && selectedLocation) {
        // Collect all valid coordinates
        const allLocations = [selectedLocation];

        uniqueTutors.forEach((tutor) => {
          if (tutor.location?.coordinates?.length === 2) {
            const val1 = parseFloat(tutor.location.coordinates[0]);
            const val2 = parseFloat(tutor.location.coordinates[1]);

            if (!isNaN(val1) && !isNaN(val2)) {
              // Auto-detect correct lat/lng
              let lat, lng;
              if (val1 >= 8 && val1 <= 35 && val2 >= 68 && val2 <= 97) {
                lat = val1;
                lng = val2;
              } else if (val2 >= 8 && val2 <= 35 && val1 >= 68 && val1 <= 97) {
                lat = val2;
                lng = val1;
              } else {
                return; // Skip invalid coordinates
              }
              allLocations.push({ lat, lng });
            }
          }
        });

        if (allLocations.length > 1) {
          // Calculate bounds
          const lats = allLocations.map((loc) => loc.lat);
          const lngs = allLocations.map((loc) => loc.lng);

          const centerLat = (Math.min(...lats) + Math.max(...lats)) / 2;
          const centerLng = (Math.min(...lngs) + Math.max(...lngs)) / 2;

          setMapCenter({ lat: centerLat, lng: centerLng });

          // Calculate appropriate zoom level based on distance
          const latDiff = Math.max(...lats) - Math.min(...lats);
          const lngDiff = Math.max(...lngs) - Math.min(...lngs);
          const maxDiff = Math.max(latDiff, lngDiff);

          let zoom = 13;
          if (maxDiff > 0.5) zoom = 10;
          else if (maxDiff > 0.2) zoom = 11;
          else if (maxDiff > 0.1) zoom = 12;
          else if (maxDiff > 0.05) zoom = 13;
          else zoom = 14;

          setMapZoom(zoom);
        }
      }

      if (uniqueTutors.length === 0) {
        setError(
          "No tutors found matching your criteria. Try increasing the radius or adjusting filters."
        );
        setShowAlert(true);
      }
    } catch (error) {
      console.error("❌ Error filtering tutors:", error);
      setError(error.response?.data?.message || "Error filtering tutors. Please try again.");
      setShowAlert(true);
    } finally {
      setLoading(false);
    }
  };

  // Format tutors data for table display
  const formatTutorsForTable = () => {
    return filteredTutors.map((tutor) => {
      // Calculate age from DOB if available
      let age = "N/A";
      if (tutor.dob) {
        const birthDate = new Date(tutor.dob);
        const today = new Date();
        age = today.getFullYear() - birthDate.getFullYear();
      }

      // Badge color mapping
      const badgeColors = {
        "Level 1": "error",
        "Level 2": "warning",
        "Level 3": "success",
        "Level 4": "info",
      };

      // Badge emoji mapping
      const badgeEmojis = {
        "Level 1": "🥉",
        "Level 2": "🥈",
        "Level 3": "🥇",
        "Level 4": "💎",
      };

      return {
        Tutor: (
          <ArgonBox display="flex" alignItems="center" px={0.5} py={0.1}>
            <ArgonBox mr={2}>
              <ArgonAvatar
                src={tutor.profileImage || defaultAvatar}
                alt={tutor.fullName}
                size="sm"
                variant="full"
              />
            </ArgonBox>
            <ArgonBox display="flex" flexDirection="column">
              <ArgonTypography variant="button" fontWeight="medium">
                <Link
                  to={`/tutors/profile/${tutor._id}`}
                  style={{ textDecoration: "underline", color: "inherit" }}
                >
                  {tutor.fullName}
                </Link>
              </ArgonTypography>
              <ArgonTypography variant="caption" color="secondary">
                {tutor.email || "No email"}
              </ArgonTypography>
            </ArgonBox>
          </ArgonBox>
        ),
        "Mobile No.": (
          <ArgonTypography variant="caption" color="text" fontWeight="medium">
            {tutor.phone}
          </ArgonTypography>
        ),
        Age: (
          <ArgonTypography variant="caption" color="text" fontWeight="medium">
            {age}
          </ArgonTypography>
        ),
        Distance: (
          <ArgonTypography variant="caption" color="text" fontWeight="medium">
            {tutor.distance ? `${tutor.distance.toFixed(1)} km` : "N/A"}
          </ArgonTypography>
        ),
        Badge: tutor.tutorLevel ? (
          <ArgonBadge
            variant="gradient"
            badgeContent={`${badgeEmojis[tutor.tutorLevel]} ${tutor.tutorLevel}`}
            color={badgeColors[tutor.tutorLevel]}
            size="xs"
            container
          />
        ) : (
          <ArgonTypography variant="caption" color="text">
            No Level
          </ArgonTypography>
        ),
        "Per Hour": (
          <ArgonTypography variant="caption" color="text" fontWeight="medium">
            ₹{tutor.hourlyRate || 0}
          </ArgonTypography>
        ),
      };
    });
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <Snackbar
        open={showAlert}
        autoHideDuration={6000}
        onClose={() => setShowAlert(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setShowAlert(false)}
          severity={error ? "error" : "info"}
          sx={{ width: "100%" }}
        >
          {error}
        </Alert>
      </Snackbar>

      <ArgonBox py={3}>
        <ArgonBox p={3}>
          <Card sx={{ boxShadow: 6, borderRadius: "20px" }}>
            {/* Header */}
            <CardHeader
              title={
                <ArgonTypography variant="body2" fontWeight="medium">
                  Shortlist Tutors
                </ArgonTypography>
              }
              sx={{ borderBottom: "1px solid #eee" }}
            />

            <CardContent>
              <Grid container spacing={3}>
                {/* Left Side - Form */}
                <Grid item xs={12} md={6}>
                  <form>
                    <Grid container spacing={2}>
                      {/* Enquiry */}
                      <Grid item xs={12} sm={6}>
                        <ArgonTypography variant="button" fontWeight="regular" color="text">
                          Parent *
                        </ArgonTypography>
                        <Dropdown
                          label=""
                          value={Enquiry}
                          onChange={(e) => setEnquiry(e.target.value)}
                          options={parents}
                          width={260}
                        />
                      </Grid>

                      {/* Student */}
                      <Grid item xs={12} sm={6}>
                        <ArgonTypography variant="button" fontWeight="regular" color="text" mb={1}>
                          Students *
                        </ArgonTypography>
                        <Autocomplete
                          multiple
                          options={students}
                          getOptionLabel={(option) => option.label || option.name || ""}
                          value={students.filter((student) =>
                            selectedStudents.includes(student.value)
                          )}
                          onChange={(event, newValue) => {
                            const selectedIds = newValue.map((student) => student.value);
                            setSelectedStudents(selectedIds);
                          }}
                          disabled={!Enquiry || students.length === 0}
                          renderTags={(value, getTagProps) =>
                            value.map((option, index) => (
                              <Chip
                                variant="outlined"
                                label={option.label || option.name}
                                {...getTagProps({ index })}
                                key={option.value}
                                size="small"
                              />
                            ))
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              variant="outlined"
                              placeholder={
                                students.length === 0 ? "No students available" : "Select students"
                              }
                              size="small"
                            />
                          )}
                          sx={{
                            width: 260,
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "8px",
                            },
                          }}
                        />
                      </Grid>

                      {/* Status */}
                      {/* <Grid item xs={12} sm={6}>
                        <ArgonTypography variant="button" fontWeight="regular" color="text">
                          Status
                        </ArgonTypography>
                        <Dropdown
                          label=""
                          value={Status}
                          onChange={(e) => setStatus(e.target.value)}
                          options={["Status 1", "Status 2", "Status 3"]}
                          width={260}
                        />
                      </Grid> */}

                      {/* Academic */}
                      <Grid item xs={12} sm={6}>
                        <ArgonTypography variant="button" fontWeight="regular" color="text">
                          Subject (Optional)
                        </ArgonTypography>
                        <Dropdown
                          label=""
                          value={Academic}
                          onChange={(e) => setAcademic(e.target.value)}
                          options={subjects}
                          width={260}
                          disabled={!selectedStudents || selectedStudents.length === 0}
                        />
                      </Grid>

                      {/* Gender */}
                      <Grid item xs={12} sm={6}>
                        <ArgonTypography variant="button" fontWeight="regular" color="text">
                          Gender
                        </ArgonTypography>
                        <Dropdown
                          label=""
                          value={Gender}
                          onChange={(e) => setGender(e.target.value)}
                          options={[
                            { label: "Male", value: "MALE" },
                            { label: "Female", value: "FEMALE" },
                            { label: "Other", value: "OTHER" },
                          ]}
                          width={260}
                        />
                      </Grid>

                      {/* Badge/Level */}
                      <Grid item xs={12} sm={6}>
                        <ArgonTypography variant="button" fontWeight="regular" color="text">
                          Level/Badge
                        </ArgonTypography>
                        <Dropdown
                          label=""
                          value={BadgeLevel}
                          onChange={(e) => setBadgeLevel(e.target.value)}
                          options={[
                            { label: "All", value: "All" },
                            { label: "🥉 Level 1", value: "Level 1" },
                            { label: "🥈 Level 2", value: "Level 2" },
                            { label: "🥇 Level 3", value: "Level 3" },
                            { label: "💎 Level 4", value: "Level 4" },
                          ]}
                          width={260}
                        />
                      </Grid>

                      {/* English Fluency */}
                      <Grid item xs={12} sm={6}>
                        <ArgonTypography variant="body2" color="text" fontWeight="medium">
                          English Fluency ({fluency})
                        </ArgonTypography>
                        <Slider
                          sx={{
                            "& .MuiSlider-track": { backgroundColor: "#1498fcff" },
                            "& .MuiSlider-thumb": { backgroundColor: "#1498fcff" },
                            "& .MuiSlider-rail": { backgroundColor: "#e0e0e0" },
                          }}
                          min={0}
                          max={10}
                          value={fluency}
                          onChange={(e, newValue) => setFluency(newValue)}
                        />
                      </Grid>

                      {/* Radius */}
                      <Grid item xs={12} sm={6}>
                        <ArgonTypography variant="body2" color="text" fontWeight="medium">
                          Radius ({radius} Km)
                        </ArgonTypography>
                        <Slider
                          sx={{
                            "& .MuiSlider-track": { backgroundColor: "#1498fcff" },
                            "& .MuiSlider-thumb": { backgroundColor: "#1498fcff" },
                            "& .MuiSlider-rail": { backgroundColor: "#e0e0e0" },
                          }}
                          min={0}
                          max={10}
                          value={radius}
                          onChange={(e, newValue) => setRadius(newValue)}
                        />
                      </Grid>

                      {/* Submit */}
                      <Grid item xs={12} sx={{ display: "flex", justifyContent: "center" }}>
                        <ArgonButton
                          variant="gradient"
                          color="info"
                          size="small"
                          onClick={handleApplyFilters}
                          disabled={
                            loading ||
                            !Enquiry ||
                            !selectedStudents ||
                            selectedStudents.length === 0
                          }
                        >
                          {loading ? (
                            <CircularProgress size={20} color="inherit" />
                          ) : (
                            "Apply Filters"
                          )}
                        </ArgonButton>
                      </Grid>
                    </Grid>
                  </form>
                </Grid>

                {/* Right Side - Map */}
                <Grid item xs={12} md={6}>
                  <ArgonTypography variant="h6" mb={2}>
                    Parent Location{" "}
                    {parentDetails?.fullName && (
                      <span>
                        (
                        <Link
                          to={`/parents/profile/${Enquiry}`}
                          style={{ textDecoration: "underline", color: "inherit" }}
                        >
                          {parentDetails.fullName}
                        </Link>
                        )
                      </span>
                    )}
                  </ArgonTypography>
                  {!Enquiry || Enquiry === "" ? (
                    <ArgonBox
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      height="400px"
                      bgcolor="#f5f5f5"
                      borderRadius="12px"
                    >
                      <ArgonTypography variant="body2" color="text" textAlign="center">
                        Please select a parent to view location
                      </ArgonTypography>
                    </ArgonBox>
                  ) : (
                    <div style={{ height: "400px", borderRadius: "12px", overflow: "hidden" }}>
                      {loadError && (
                        <ArgonBox
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          height="100%"
                          bgcolor="#f5f5f5"
                          p={3}
                        >
                          <ArgonTypography variant="body2" color="error" textAlign="center">
                            Map cannot be loaded. Please check your internet connection.
                          </ArgonTypography>
                        </ArgonBox>
                      )}
                      {!isLoaded && !loadError && (
                        <ArgonBox
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          height="100%"
                          bgcolor="#f5f5f5"
                        >
                          <CircularProgress size={40} />
                        </ArgonBox>
                      )}
                      {isLoaded && !loadError && (
                        <GoogleMap
                          mapContainerStyle={{ width: "100%", height: "100%" }}
                          center={mapCenter}
                          zoom={mapZoom}
                          onLoad={(map) => {
                            mapRef.current = map;
                          }}
                          options={{
                            streetViewControl: false,
                            mapTypeControl: false,
                            fullscreenControl: false,
                            draggable: true,
                            zoomControl: true,
                            scrollwheel: true,
                          }}
                        >
                          {/* Parent Location Marker */}
                          {selectedLocation && (
                            <>
                              <OverlayView
                                position={selectedLocation}
                                mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                              >
                                <div
                                  style={{
                                    position: "relative",
                                    transform: "translate(-50%, -100%)",
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                  }}
                                >
                                  <img
                                    src={parentDetails?.profileImage || defaultAvatar}
                                    alt={parentDetails?.fullName}
                                    style={{
                                      width: "50px",
                                      height: "50px",
                                      borderRadius: "50%",
                                      border: "3px solid #FF0000",
                                      objectFit: "cover",
                                      backgroundColor: "white",
                                      cursor: "default",
                                    }}
                                  />
                                  <div
                                    style={{
                                      marginTop: "5px",
                                      backgroundColor: "white",
                                      padding: "8px 12px",
                                      borderRadius: "10px",
                                      boxShadow: "0 2px 6px rgba(0,0,0,0.3)",
                                      whiteSpace: "nowrap",
                                      textAlign: "center",
                                      minWidth: "120px",
                                    }}
                                  >
                                    <div
                                      style={{
                                        fontWeight: "bold",
                                        fontSize: "12px",
                                        color: "#FF0000",
                                      }}
                                    >
                                      {parentDetails?.fullName || "Parent"}
                                    </div>
                                  </div>
                                </div>
                              </OverlayView>
                            </>
                          )}

                          {/* Tutor Location Markers */}
                          {filteredTutors.map((tutor) => {
                            // Check if tutor has location data
                            if (!tutor.location || !tutor.location.coordinates) {
                              return null;
                            }

                            const coords = tutor.location.coordinates;

                            // Validate coordinates array
                            if (!Array.isArray(coords) || coords.length !== 2) {
                              return null;
                            }

                            // Extract values
                            const val1 = parseFloat(coords[0]);
                            const val2 = parseFloat(coords[1]);

                            // Validate numeric values
                            if (isNaN(val1) || isNaN(val2)) {
                              return null;
                            }

                            // Auto-detect correct lat/lng based on typical ranges
                            // For India: Lat ~8-35, Lng ~68-97
                            let lat, lng;

                            if (val1 >= 8 && val1 <= 35 && val2 >= 68 && val2 <= 97) {
                              lat = val1;
                              lng = val2;
                            } else if (val2 >= 8 && val2 <= 35 && val1 >= 68 && val1 <= 97) {
                              lat = val2;
                              lng = val1;
                            } else {
                              return null;
                            }

                            return (
                              <React.Fragment key={tutor._id}>
                                <OverlayView
                                  position={{ lat, lng }}
                                  mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                                >
                                  <div
                                    style={{
                                      position: "relative",
                                      transform: "translate(-50%, -100%)",
                                      display: "flex",
                                      flexDirection: "column",
                                      alignItems: "center",
                                    }}
                                  >
                                    <img
                                      src={tutor.profileImage || defaultAvatar}
                                      alt={tutor.fullName}
                                      style={{
                                        width: "50px",
                                        height: "50px",
                                        borderRadius: "50%",
                                        border: "3px solid #1498FC",
                                        objectFit: "cover",
                                        backgroundColor: "white",
                                        cursor: "default",
                                      }}
                                    />
                                    <div
                                      style={{
                                        marginTop: "5px",
                                        backgroundColor: "white",
                                        padding: "8px 12px",
                                        borderRadius: "10px",
                                        boxShadow: "0 2px 6px rgba(0,0,0,0.3)",
                                        whiteSpace: "nowrap",
                                        textAlign: "center",
                                        minWidth: "120px",
                                      }}
                                    >
                                      <div
                                        style={{
                                          fontWeight: "bold",
                                          fontSize: "12px",
                                          color: "#1498FC",
                                          marginBottom: "4px",
                                        }}
                                      >
                                        {tutor.fullName || "Tutor"}
                                      </div>
                                      <div
                                        style={{
                                          fontSize: "11px",
                                          color: "#7b809a",
                                        }}
                                      >
                                        {tutor.distance ? `${tutor.distance.toFixed(1)} km` : "N/A"}
                                      </div>
                                    </div>
                                  </div>
                                </OverlayView>
                              </React.Fragment>
                            );
                          })}
                        </GoogleMap>
                      )}
                    </div>
                  )}
                  {selectedLocation && parentDetails && (
                    <ArgonBox mt={2}>
                      <ArgonTypography variant="body2" color="text">
                        {parentDetails.area && `${parentDetails.area}, `}
                        {parentDetails.city}
                      </ArgonTypography>
                      <ArgonTypography variant="caption" color="secondary">
                        Coordinates: {selectedLocation.lat.toFixed(6)},{" "}
                        {selectedLocation.lng.toFixed(6)}
                      </ArgonTypography>
                    </ArgonBox>
                  )}
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </ArgonBox>

        {/* Table Section */}
        <ArgonBox mt={3} p={3}>
          <Card sx={{ boxShadow: 6, borderRadius: "20px" }}>
            <CardHeader
              title={
                <ArgonTypography variant="body2" fontWeight="medium">
                  Filtered Tutors ({filteredTutors.length})
                </ArgonTypography>
              }
              sx={{ borderBottom: "1px solid #eee" }}
            />
            <CardContent>
              {loading ? (
                <ArgonBox display="flex" justifyContent="center" alignItems="center" p={5}>
                  <CircularProgress />
                </ArgonBox>
              ) : filteredTutors.length > 0 ? (
                <ArgonBox
                  sx={{
                    "& .MuiTableRow-root:not(:last-child)": {
                      "& td": {
                        borderBottom: ({ borders: { borderWidth, borderColor } }) =>
                          `${borderWidth[1]} solid ${borderColor}`,
                      },
                    },
                  }}
                >
                  <Table columns={columns} rows={formatTutorsForTable()} />
                </ArgonBox>
              ) : (
                <ArgonBox display="flex" justifyContent="center" alignItems="center" p={5}>
                  <ArgonTypography variant="body2" color="text">
                    No tutors found. Please apply filters to see results.
                  </ArgonTypography>
                </ArgonBox>
              )}
            </CardContent>
          </Card>
        </ArgonBox>
      </ArgonBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Tables;
